import { GLOBAL_TAG, CREATED, DELETED, UPDATED } from './TOKENS.js';
import { dispatch } from './marker.js';

export const OPERATION_INVERSES = {
  [CREATED]: DELETED,
  [DELETED]: CREATED,
  [UPDATED]: UPDATED,
}

/**
 * Determines the operation type based on from/to values
 * 
 * @private
 * @param {*} from - Previous value
 * @param {*} to - New value
 * @returns {'created'|'deleted'|'updated'} Operation type
 */
function determineOperation(to, from) {
  if (from === undefined) return CREATED;
  if (to === undefined) return DELETED;
  return UPDATED;
}

/**
 * Creates a change object with the given parameters
 * 
 * @param {string} method - Method that triggered the change
 * @param {*} from - Previous value
 * @param {*} to - New value
 * @returns {Object} Change object
 * @example
 * createChange('set', undefined, 'new value') 
 * // => { method: 'set', operation: 'created', from: undefined, to: 'new value' }
 */
export function createChange(marker, method, to, from) {
  return dispatch(marker, {
    global: () => ({
      path: GLOBAL_TAG,
      method,
      operation: determineOperation(to, from),
      from,
      to
    }),
    path: (marker) => ({
      path: marker.path,
      method,
      operation: determineOperation(to, from),
      from,
      to
    }),
  });
}

/**
 * Inverts a change object for undo operations
 * 
 * @param {Object} change - Change object to invert
 * @param {string} change.method - Original method
 * @param {string} change.operation - Original operation
 * @param {*} change.from - Original from value
 * @param {*} change.to - Original to value
 * @returns {Object} Inverted change object
 * @example
 * invertChange({ method: 'set', operation: 'created', from: undefined, to: 'value' })
 * // => { method: 'set', operation: 'deleted', from: 'value', to: undefined }
 */
export function invertChange(change) {
  const {address, path, method, from, to, operation } = change;
  return {
    address,
    path,
    method,
    to: from,
    from: to,
    operation: invertOperation(operation),
  }
}

export function invertOperation(operation) {
  return OPERATION_INVERSES[operation];
}

/**
 * Checks if a change needs to be inverted based on the method
 * 
 * @param {string} method - Method to check
 * @returns {boolean} True if the change should be inverted
 * @example
 * shouldInvertChange('undo') // => true
 * shouldInvertChange('set') // => false
 */
export function shouldInvertChange(method) {
  return ['undo', 'redo', 'stepBackward', 'stepForward'].includes(method);
}